import { getCategories } from '../config/runtimeListingConfig';

/**
 * Category type-ahead suggestions for the takeover search (browse redesign
 * Phase 5, ledger §4). Text-only by decision: name + ancestor path.
 *
 * Matching is by category NAME (case-insensitive substring). Synonyms are
 * follow-up scope (plan "Follow-up scope" §2) and drop into this matcher as
 * extra match terms when authored — the ranking and shape stay the same.
 */

const flatten = (cats, path = [], acc = []) => {
  cats.forEach(cat => {
    acc.push({ category: cat, path });
    if (cat.subcategories?.length > 0) {
      flatten(cat.subcategories, [...path, cat.name], acc);
    }
  });
  return acc;
};

/**
 * Suggest category homes for a search term.
 *
 * Ranking: name starts-with beats substring; shallower categories beat deeper
 * ones (family homes are better landing points than leaf variants).
 *
 * @returns Array of { category, path: [ancestor names], matchIndex }
 */
export const suggestCategories = (term, cats = getCategories(), limit = 6) => {
  const trimmed = (term || '').trim().toLowerCase();
  if (trimmed.length < 2) return [];

  const all = flatten(cats);
  const matches = all
    .map(entry => {
      const name = entry.category.name || '';
      const matchIndex = name.toLowerCase().indexOf(trimmed);
      return { ...entry, matchIndex };
    })
    .filter(entry => entry.matchIndex >= 0);

  matches.sort((a, b) => {
    const aStarts = a.matchIndex === 0 ? 0 : 1;
    const bStarts = b.matchIndex === 0 ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    const aDepth = a.path.length;
    const bDepth = b.path.length;
    if (aDepth !== bDepth) return aDepth - bDepth;
    // Equal rank keeps config file order (stable sort) — matches the
    // prototype and the ledger's "file order preserved" principle
    return 0;
  });

  return matches.slice(0, limit);
};

/**
 * The best category home for a term — used by the zero-results page to route
 * shoppers somewhere useful. Falls back across the term's words.
 */
export const closestCategoryHome = (term, cats = getCategories()) => {
  const direct = suggestCategories(term, cats, 1);
  if (direct.length > 0) return direct[0].category;

  const words = (term || '').trim().split(/\s+/).filter(w => w.length >= 3);
  for (const word of words) {
    const byWord = suggestCategories(word, cats, 1);
    if (byWord.length > 0) return byWord[0].category;
  }
  return null;
};
